import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowRight,
  Check,
  Phone,
  Shield,
  Award,
  Clock,
  MapPin,
  Stethoscope,
  Scale,
  Building2,
  Briefcase,
  Star,
} from "lucide-react";
import { companyInfo, testimonials } from "../data/mock";
import { seoPages } from "../data/seo-pages";
import { useScrollReveal } from "../hooks/useScrollReveal";

const iconMap = { Stethoscope, Scale, Building2, Briefcase };

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function SEOLandingPage() {
  const { pageId } = useParams();
  const page = seoPages.find((p) => p.id === pageId);

  if (!page) {
    return <Navigate to="/" replace />;
  }

  const Icon = iconMap[page.icon] || Briefcase;

  // Pick a relevant testimonial
  const relevantTestimonial = page.icon === "Stethoscope"
    ? testimonials[0]
    : page.icon === "Scale"
    ? testimonials[1]
    : page.icon === "Building2"
    ? testimonials[2]
    : testimonials[3];

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="container mx-auto px-6 page-enter">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Icon className="w-6 h-6 text-blue-600" />
              </div>
              <Badge
                variant="secondary"
                className="px-3 py-1 text-blue-700 bg-blue-50 border border-blue-100 text-xs font-medium uppercase tracking-wider"
              >
                Australian IT Support
              </Badge>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6 leading-[1.1]">
              {page.title}
            </h1>

            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed mb-8 max-w-3xl">
              {page.intro}
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-7 text-base"
              >
                <Link to="/contact">
                  {page.ctaButton}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-7 text-base border-slate-300 text-slate-800 hover:bg-slate-50 font-semibold"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2 text-blue-600" />
                  Call {companyInfo.phone}
                </a>
              </Button>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1.5">
                <Award className="w-4 h-4 text-blue-600" />
                20+ Years Experience
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-blue-600" />
                Local Australian Team
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-blue-600" />
                15 Min Avg Response
              </span>
              <span className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-blue-600" />
                No Lock-In Contracts
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose + Services */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Why Choose */}
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight mb-6">
                {page.whyTitle}
              </h2>
              <div className="space-y-4">
                {page.whyPoints.map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-blue-700" />
                    </div>
                    <span className="text-slate-700 leading-relaxed text-base">
                      {point}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Services */}
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight mb-6">
                {page.servicesTitle}
              </h2>
              <div className="space-y-3">
                {page.services.map((service, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100"
                  >
                    <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span className="text-slate-700 font-medium text-[0.95rem]">
                      {service}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Compliance / Security section */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
                {page.complianceTitle}
              </h2>
            </div>
            <p className="text-lg text-slate-600 leading-relaxed">
              {page.complianceText}
            </p>
          </div>
        </div>
      </Section>

      {/* Testimonial */}
      <Section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <Card className="border-slate-100">
              <CardContent className="p-8 lg:p-10">
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: relevantTestimonial.rating }).map(
                    (_, j) => (
                      <Star
                        key={j}
                        className="w-5 h-5 fill-amber-400 text-amber-400"
                      />
                    )
                  )}
                </div>
                <blockquote className="text-lg text-slate-700 leading-relaxed mb-6">
                  &ldquo;{relevantTestimonial.quote}&rdquo;
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-700">
                    {relevantTestimonial.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">
                      {relevantTestimonial.name}
                    </div>
                    <div className="text-sm text-slate-500">
                      {relevantTestimonial.role},{" "}
                      {relevantTestimonial.company}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-slate-900 relative grain-overlay">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-5">
              {page.ctaText}
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              No lock-in contracts. No hidden fees. Just honest, reliable IT
              support from a local Australian team.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8 text-base"
              >
                <Link to="/contact">
                  {page.ctaButton}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-8 text-base border-slate-600 text-white hover:bg-slate-800 hover:text-white"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Other SEO pages */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">
            Other Industries We Support
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {seoPages
              .filter((p) => p.id !== pageId)
              .map((p) => {
                const PIcon = iconMap[p.icon] || Briefcase;
                return (
                  <Link key={p.id} to={`/seo/${p.id}`}>
                    <Card className="service-card h-full border-slate-200/60 bg-white group cursor-pointer hover:border-blue-100">
                      <CardContent className="p-6">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors duration-300">
                          <PIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">
                          {p.title}
                        </h3>
                        <span className="text-sm text-blue-600 font-medium">
                          Learn more \u2192
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
          </div>
        </div>
      </Section>
    </div>
  );
}
